package pl.decks.personal.pages.ievp

import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.border
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.*
import pl.decks.personal.components.graphics.heropatterns.HeroPattern
import pl.decks.personal.components.graphics.heropatterns.heroPatternBackground
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet
import pl.decks.personal.components.widgets.disclaimer.DisclaimerBoxStylesheet
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.pages.ievp.sections.background.BackgroundSectionStylesheet
import pl.decks.personal.pages.ievp.sections.why.WhySectionStylesheet
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout
import pl.decks.personal.components.widgets.pageprogress.PageProgressPointsBarStylesheet.Vars as PointsBarVars

object IEVPStylesheet {
    object Vars {
        val contentPaddingLeft by StyleVariable(SiteLayout.Space.Small)
        val contentHeaderOpacity by StyleVariable(SiteLayout.Fraction.All)
    }

    object Section {
        sealed interface Kind : ComponentKind

        val Style = CssStyle.base<Kind> {
            Modifier.fillMaxWidth().overflow { x(Overflow.Clip) }
        }
        val TransparentVariant = Style.addVariantBase {
            Modifier.backgroundColor(Colors.Transparent)
        }
        val CoveredVariant = Style.addVariantBase {
            Modifier.backgroundColor(colorMode.toPalette().background)
        }
        val NormalTextVariant = Style.addVariantBase {
            Modifier.color(colorMode.toPalette().color)
        }
        val InvertedTextVariant = Style.addVariantBase {
            Modifier.color(colorMode.toPalette().color.inverted())
        }
    }

    object Content {
        val Container = SiteLayoutStylesheet.Page.Content.extendedByBase {
            Modifier
                .padding(
                    left = Vars.contentPaddingLeft.value(),
                    right = SiteLayout.Space.Small,
                    top = SiteLayout.Space.XLarge,
                    bottom = SiteLayout.Space.XLarge,
                )
                .gap(SiteLayout.Space.Medium)
        }

        val Header = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .gap(SiteLayout.Space.XXSmall)
                .opacity(Vars.contentHeaderOpacity.value())
        }

        val Title = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fillMaxWidth()
                .textAlign(TextAlign.Right)
                .fontSize(FontTypography.Size.XXXLarge)
                .lineHeight(FontTypography.LineHeight.XXSmall)
                .fontWeight(FontTypography.Weight.Bold)
        }

        val Flavour = FontTypography.mono.extendedByBase {
            Modifier
                .fillMaxWidth()
                .textAlign(TextAlign.Right)
                .fontSize(FontTypography.Size.Small)
                .lineHeight(FontTypography.LineHeight.Large)
                .fontWeight(FontTypography.Weight.Light)
        }
    }

    object Background {
        val Why = CssStyle.base {
            Modifier.fillMaxSize()
                .backgroundColor(colorMode.toSitePalette().surface)
                .heroPatternBackground(
                    HeroPattern.TOPOGRAPHY,
                    color = colorMode.toSitePalette().accents.secondary.toRgb().copyf(alpha = 0.15f),
                )
        }
        val Pitch = CssStyle.base {
            Modifier.fillMaxSize()
                .backgroundColor(colorMode.toSitePalette().surface)
        }
        val LastYears = CssStyle.base {
            val sitePalette = colorMode.toSitePalette()
            Modifier
                .fillMaxSize()
                .backgroundColor(sitePalette.surface)
                .padding(left = Vars.contentPaddingLeft.value() / 2)
                .heroPatternBackground(
                    HeroPattern.TEXTURE,
                    color = sitePalette.accents.secondary.toRgb().copyf(alpha = 0.25f),
                    scale = 2.0,
                )
        }
        val Background = CssStyle {
            base {
                Modifier
                    .fillMaxSize()
                    .backgroundColor(colorMode.toSitePalette().surface)
                    .fontSize(13.em)
                    .color(colorMode.toPalette().background)
                    .heroPatternBackground(
                        HeroPattern.GRAPH_PAPER,
                        color = colorMode.toPalette().border,
                        scale = 1.5,
                    )
            }
            Breakpoint.MD {
                Modifier
                    .fontSize(30.em)
                    .heroPatternBackground(
                        HeroPattern.GRAPH_PAPER,
                        color = colorMode.toPalette().border,
                        scale = 2.0,
                    )
            }
        }
    }

    val Page = CssStyle {
        val sitePalette = colorMode.toSitePalette()
        val accent = sitePalette.accents.primary.toRgb()
        val approxScreenSpace = SiteLayoutStylesheet.Vars.approxScreenSpace.value()
        base {
            Modifier
                .fillMaxWidth()
                .setVariable(DisclaimerBoxStylesheet.Vars.disclaimerAccentColor, accent)
                .setVariable(PointsBarVars.color, accent)
                .setVariable(PointsBarVars.activeColor, accent)
                .setVariable(PointsBarVars.futureColor, accent.copyf(alpha = 0.5f))
                .setVariable(PointsBarVars.height, approxScreenSpace)
                .setVariable(
                    Vars.contentPaddingLeft,
                    SiteLayout.Space.XXSmall +
                            PointsBarVars.activePointSize.value() +
                            PointsBarVars.marginLeft.value(),
                )
                .setVariable(WhySectionStylesheet.Vars.emphasisColor, sitePalette.accents.secondary)
                .setVariable(WhySectionStylesheet.Vars.segmentHeight, 38.vh)
                .setVariable(BackgroundSectionStylesheet.Vars.entryAccentColor, accent)
                .setVariable(BackgroundSectionStylesheet.Vars.drawBackgroundColor, sitePalette.surface)
                .setVariable(BackgroundSectionStylesheet.Vars.canvasDrawStart, SiteLayout.Space.XLarge)
                .setVariable(BackgroundSectionStylesheet.Vars.mainIconSize, 3.cssRem)
        }
        Breakpoint.MD {
            Modifier
                .setVariable(PointsBarVars.pointSize, 26.px)
                .setVariable(PointsBarVars.activePointSize, 40.px)
                .setVariable(WhySectionStylesheet.Vars.segmentHeight, 35.vh)
                .setVariable(BackgroundSectionStylesheet.Vars.canvasDrawStart, 5.vh)
                .setVariable(BackgroundSectionStylesheet.Vars.mainIconSize, 4.cssRem)
        }
    }

    val SupererogatorySection = CssStyle.base {
        Modifier.fillMaxWidth().padding(topBottom = 2 * SiteLayout.Space.XXXLarge)
    }

    val SupererogatoryWords = FontTypography.sansSerif.extendedByBase {
        Modifier
            .fontWeight(FontTypography.Weight.Normal)
            .fontSize(FontTypography.Size.Normal)
            .maxWidth(SiteLayout.Breakpoint.sm)
            .textAlign(TextAlign.Center)
            .padding(leftRight = SiteLayout.Space.XXXLarge)
    }

    val ContactMail = FontTypography.sansSerif.extendedByBase {
        Modifier
            .color(colorMode.toSitePalette().accents.secondary)
            .fontSize(FontTypography.Size.Large)
    }

    object SummaryMailInput {
        object Vars {
            val borderColor by StyleVariable<Color>()
            val feedbackColor by StyleVariable<Color>()
        }

        val Container = CssStyle.base {
            Modifier
                .maxWidth(SiteLayout.Breakpoint.sm)
                .padding(leftRight = SiteLayout.Space.XXXLarge)
                .overflow(Overflow.Clip)
                .gap(SiteLayout.Space.Small)
        }

        val Field = CssStyle.base {
            Modifier
                .padding(leftRight = SiteLayout.Space.Small, topBottom = SiteLayout.Space.XSmall)
                .border(
                    width = SiteLayout.Line.Hairline,
                    style = LineStyle.Solid,
                    color = Vars.borderColor.value(colorMode.toPalette().border),
                )
        }

        val Feedback = FontTypography.sansSerif.extendedByBase {
            Modifier
                .fontSize(FontTypography.Size.Small)
                .maxWidth(SiteLayout.Breakpoint.sm)
                .textAlign(TextAlign.Center)
                .padding(leftRight = SiteLayout.Space.XXXLarge)
                .color(Vars.feedbackColor.value(colorMode.toSitePalette().accents.secondary))
        }
    }
}
